import { Button } from '@studio/design-system/button';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { ReactElement } from 'react';
import { Sel, STagNew } from '#~/stag/tag.js';
import { useAutoFocus } from '../hooks/use-auto-focus.js';
import { openBridgeHelpArticle } from '../help-actions.js';
import { FunnelShell } from './funnel-shell.js';
import { useStepView } from '../hooks/use-step-view.js';

interface FunnelBridgeConceptProps {
  readonly onNext: () => void;
  readonly onSkip: () => void;
  /** Escape / the close affordance: dismiss WITHOUT recording the permanent
   *  skip. Distinct from onSkip, which is an explicit user decision to defer —
   *  see gate.tsx handleDismiss (ENG-3796 live-run defect 1). */
  readonly onDismiss: () => void;
}

// Screen 2 of the bridge-connection funnel (RFD 0006). Introduces the
// Bridge mental model — local software, network, cloud relay — before any
// connection attempt so the user has scaffolding if the failure path fires.
export const FunnelBridgeConcept = ({
  onNext,
  onSkip,
  onDismiss
}: FunnelBridgeConceptProps): ReactElement => {
  useStepView('bridge-concept');

  const ctaRef = useAutoFocus<HTMLButtonElement>();

  return (
    <FunnelShell announceTitle="What's a Bridge?" onEscape={onDismiss}>
      <h1 className='text-2xl font-demibold text-white'>What's a Bridge?</h1>

      <p className='mt-12 max-w-[28rem] text-md text-foreground/75'>
        A Bridge runs on this computer and links it to your {vendorName()} Studio in the cloud. It's
        how {vendorName()} discovers cameras on your network, streams their video, and keeps
        everything secure — even when you're not at your desk.
      </p>

      <Button
        className='mt-12'
        {...STagNew(Sel.OnboardingBridgeConcept, 'learn_more')}
        onClick={openBridgeHelpArticle}
        size='sm'
        variant='tertiary'
      >
        Learn more →
      </Button>

      <BridgeTopologyDiagram />

      <Button
        ref={ctaRef}
        className='mt-32'
        {...STagNew(Sel.OnboardingBridgeConcept, 'setup')}
        onClick={onNext}
        size='lg'
        variant='primary'
      >
        Set up your Bridge
      </Button>

      <button
        className='mt-16 text-xs text-secondary opacity-50 transition-opacity hover:opacity-75'
        {...STagNew(Sel.OnboardingBridgeConcept, 'skip')}
        onClick={onSkip}
        type='button'
      >
        Skip to dashboard
      </button>
    </FunnelShell>
  );
};

// Three-node sketch — `Cloud ↔ Bridge ↔ Devices` — using plain text and
// CSS. The Bridge node is highlighted because the screen is teaching what
// that middle node is.
const BridgeTopologyDiagram = (): ReactElement => {
  return (
    <div className='mt-32 flex w-full items-center gap-8'>
      <DiagramNode icon='cloud' label={`${vendorName()} Cloud`} delayMs={0} />
      <DiagramEdge delayMs={150} />
      <DiagramNode icon='bridge' label='Bridge' highlighted delayMs={300} />
      <DiagramEdge delayMs={450} />
      <DiagramNode icon='camera' label='Your cameras' delayMs={600} />
    </div>
  );
};

const DiagramNode = ({
  highlighted = false,
  icon,
  label,
  delayMs = 0
}: {
  readonly highlighted?: boolean;
  readonly icon: 'cloud' | 'bridge' | 'camera';
  readonly label: string;
  readonly delayMs?: number;
}): ReactElement => {
  return (
    <div
      className='flex flex-1 flex-col items-center gap-8'
      style={{ opacity: 0, animation: `fade-in 375ms ease-out ${delayMs}ms forwards` }}
    >
      <div
        className={`flex h-48 w-48 items-center justify-center rounded-full ${
          highlighted ? 'bg-primary/15 ring-primary/30 ring' : 'bg-white/5'
        }`}
      >
        {icon === 'cloud' && <CloudIcon />}
        {icon === 'bridge' && <BridgeIcon />}
        {icon === 'camera' && <CameraIcon />}
      </div>
      <span className={`text-xs ${highlighted ? 'font-medium text-primary' : 'text-muted'}`}>
        {label}
      </span>
    </div>
  );
};

const DiagramEdge = ({ delayMs = 0 }: { readonly delayMs?: number }): ReactElement => (
  <div
    className='flex flex-1 items-center'
    style={{ opacity: 0, animation: `fade-in 375ms ease-out ${delayMs}ms forwards` }}
  >
    <div
      aria-hidden='true'
      className='h-px w-full bg-gradient-to-r from-transparent via-secondary to-transparent'
    />
  </div>
);

const CloudIcon = (): ReactElement => (
  <svg
    viewBox='0 0 24 24'
    className='h-20 w-20 text-foreground/75'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.5'
  >
    <path
      d='M6.5 19h11a4.5 4.5 0 001.338-8.793A5.5 5.5 0 007.59 9.293 3.5 3.5 0 006.5 16v3z'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const BridgeIcon = (): ReactElement => (
  <svg
    viewBox='0 0 24 24'
    className='h-20 w-20 text-primary'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.5'
  >
    <rect x='4' y='8' width='16' height='8' rx='2' strokeLinecap='round' />
    <path d='M8 8V6a4 4 0 018 0v2' strokeLinecap='round' />
    <circle cx='9' cy='12' r='1' fill='currentColor' />
    <circle cx='15' cy='12' r='1' fill='currentColor' />
  </svg>
);

const CameraIcon = (): ReactElement => (
  <svg
    viewBox='0 0 24 24'
    className='h-20 w-20 text-foreground/75'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.5'
  >
    <rect x='3' y='7' width='14' height='10' rx='2' strokeLinecap='round' />
    <path d='M17 10l4-2v8l-4-2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);
